import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../config';
import { clearAuth, getToken, getUser } from '../auth';
import '../styles/Messages.css';

function Messages() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const token = getToken();
  const currentUserId = Number(user?.id);
  const expectedPartnerRole = user?.role === 'hirer' ? 'technician' : 'hirer';
  
  // Get the technician to start conversation with (from navigation state)
  const startWith = location.state?.startConversationWith;

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [techMenuOpen, setTechMenuOpen] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null); // {file, url, type}
  const [uploading, setUploading] = useState(false);
  const [partnerPhone, setPartnerPhone] = useState(null);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const selectedConvRef = useRef(null);
  const fileInputRef = useRef(null);

  const showNotice = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 2500);
  };


  const fetchConversations = useCallback(async (showLoader = true) => {
    if (showLoader) setLoadingConvs(true);
    try {
      const endpoint = user.role === 'hirer' ? '/hirer/messages' : '/technician/messages';
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuth();
          navigate(`/${user.role}/signin`);
          return;
        }
        return;
      }

      const data = await res.json();
      const grouped = {};

      data.messages.forEach((msg) => {
        if ((msg.message_type === 'system' || msg.sender_role === 'system') && Number(msg.sender_id) === 0) {
          return;
        }

        const senderId = Number(msg.sender_id);
        const receiverId = Number(msg.receiver_id);
        const isMine = senderId === currentUserId;
        const partnerId = isMine ? receiverId : senderId;
        const partnerName = isMine ? msg.receiver_name : msg.sender_name;

        if (!grouped[partnerId]) {
          grouped[partnerId] = {
            id: String(partnerId),
            partnerId,
            partnerName,
            partnerRole: (isMine ? msg.receiver_role : msg.sender_role) || expectedPartnerRole,
            lastMessage: msg.content || '',
            lastTime: msg.created_at,
            unreadCount: 0,
          };
        }
        if (new Date(msg.created_at) > new Date(grouped[partnerId].lastTime)) {
          grouped[partnerId].lastMessage = msg.content || '';
          grouped[partnerId].lastTime = msg.created_at;
        }
        if (receiverId === currentUserId && !msg.is_read) {
          grouped[partnerId].unreadCount++;
        }
      });

      setConversations(
        Object.values(grouped).sort((a, b) => new Date(b.lastTime) - new Date(a.lastTime))
      );
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      if (showLoader) setLoadingConvs(false);
    }
  }, [token, user.role, currentUserId, navigate, expectedPartnerRole]);

  const fetchMessages = useCallback(async (conv, showLoader = true) => {
    if (!conv?.partnerId) return;
    if (showLoader) setLoadingMsgs(true);
    try {
      const endpoint =
        user.role === 'hirer'
          ? `/hirer/messages/${conv.partnerId}`
          : `/technician/messages/${conv.partnerId}`;
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuth();
          navigate(`/${user.role}/signin`);
          return;
        }
        return;
      }

      const data = await res.json();
      setMessages(data.messages || []);
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0 } : c))
      );
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      if (showLoader) setLoadingMsgs(false);
    }
  }, [token, user.role, navigate]);

  useEffect(() => {
    if (!token || !user.role) {
      navigate('/hirer/signin');
      return;
    }
    fetchConversations();
    pollRef.current = setInterval(() => {
      fetchConversations(false);
      if (selectedConvRef.current) fetchMessages(selectedConvRef.current, false);
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  // Auto-create and select conversation when coming from technician profile
  useEffect(() => {
    if (startWith && startWith.id) {
      const newConv = {
        id: String(startWith.id),
        partnerId: startWith.id,
        partnerName: startWith.name,
        partnerRole: startWith.role || 'technician',
        lastMessage: '',
        lastTime: new Date().toISOString(),
        unreadCount: 0,
      };
      
      setConversations((prev) => {
        const exists = prev.find((c) => c.partnerId === startWith.id);
        if (exists) {
          setSelectedConv(exists);
          fetchMessages(exists, false);
          setMobileShowChat(true);
          return prev;
        }
        const updated = [newConv, ...prev];
        setSelectedConv(newConv);
        setMobileShowChat(true);
        return updated;
      });

      window.history.replaceState({}, document.title);
      setTimeout(() => fetchConversations(false), 500);
    }
  }, [startWith, fetchMessages, fetchConversations]);

  useEffect(() => {
    if (!selectedConv && conversations.length > 0 && user.role === 'technician' && !startWith) {
      const firstConversation = conversations[0];
      setSelectedConv(firstConversation);
      fetchMessages(firstConversation, false);
      setMobileShowChat(true);
    }
  }, [conversations, selectedConv, user.role, startWith, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectConversation = (conv) => {
    setSelectedConv(conv);
    fetchMessages(conv);
    setMobileShowChat(true);
    setTechMenuOpen(false);
  };

  const handleTechMenuNavigate = (targetPath) => {
    setTechMenuOpen(false);
    navigate(targetPath);
  };

  const handleSignOut = () => {
    clearAuth();
    navigate('/');
  };

  // Fetch partner phone when conversation selected
  useEffect(() => {
    const hasActiveHire = messages.some((m) => {
      if (!(m.message_type === 'system' || m.sender_role === 'system')) return false;
      const content = m.content || '';
      const activeMatch = content.match(/(?:^|\s)\[hire:(\d+)\]/);
      if (!activeMatch?.[1]) return false;
      return !messages.some((x) => {
        if (!(x.message_type === 'system' || x.sender_role === 'system')) return false;
        const completeMatch = (x.content || '').match(/(?:^|\s)\[hire-complete:(\d+)\]/);
        return completeMatch?.[1] === activeMatch[1];
      });
    });

    if (!selectedConv || user.role !== 'hirer' || selectedConv.partnerRole !== 'technician' || !hasActiveHire) {
      setPartnerPhone(null);
      return;
    }
    const fetchPhone = async () => {
      try {
        if (selectedConv.partnerRole === 'technician') {
          const res = await fetch(`${API_BASE_URL}/hirer/technician/${selectedConv.partnerId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const d = await res.json();
            setPartnerPhone(d.phone || null);
          }
        } else {
          setPartnerPhone(null);
        }
      } catch { setPartnerPhone(null); }
    };
    fetchPhone();
  }, [selectedConv, token, messages, user.role]);

  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      showNotice('error', 'Only images and videos are allowed.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showNotice('error', 'File must be under 25 MB.');
      return;
    }
    setMediaPreview({ file, url: URL.createObjectURL(file), type: isImage ? 'image' : 'video' });
  };

  const clearMediaPreview = () => {
    if (mediaPreview?.url) URL.revokeObjectURL(mediaPreview.url);
    setMediaPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadMedia = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = user.role === 'hirer' ? '/hirer/message/upload-media' : '/technician/message/upload-media';
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) throw new Error('Upload failed');
    return res.json(); // { media_url, media_type }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    const hasText = newMessage.trim().length > 0;
    const hasMedia = !!mediaPreview;
    if ((!hasText && !hasMedia) || !selectedConv || sending) return;

    const content = newMessage.trim() || (mediaPreview ? `[${mediaPreview.type}]` : '');
    setNewMessage('');
    setSending(true);

    let mediaUrl = null;
    let messageType = 'text';

    try {
      if (hasMedia) {
        setUploading(true);
        const uploadResult = await uploadMedia(mediaPreview.file);
        mediaUrl = uploadResult.media_url;
        messageType = uploadResult.media_type;
        setUploading(false);
        clearMediaPreview();
      }

      const optimistic = {
        id: `temp_${Date.now()}`,
        sender_id: currentUserId,
        sender_role: user.role,
        sender_name: user.full_name || 'You',
        receiver_id: selectedConv.partnerId,
        receiver_role: expectedPartnerRole,
        receiver_name: selectedConv.partnerName,
        content,
        message_type: messageType,
        media_url: mediaUrl,
        is_read: false,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);

      const endpoint = user.role === 'hirer' ? '/hirer/message/send' : '/technician/message/send';
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          receiver_id: selectedConv.partnerId,
          receiver_role: expectedPartnerRole,
          content,
          message_type: messageType,
          media_url: mediaUrl,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setNewMessage(hasText ? content : '');
        showNotice('error', errData.detail || 'Failed to send. Please try again.');
        return;
      }

      await fetchMessages(selectedConv, false);
      await fetchConversations(false);
    } catch {
      setMessages((prev) => prev.filter((m) => !String(m.id || '').startsWith('temp_')));
      setNewMessage(hasText ? content : '');
      showNotice('error', 'Network error. Please try again.');
      setUploading(false);
      clearMediaPreview();
    } finally {
      setSending(false);
    }
  };

  const handleApproveHire = async (hireId) => {
    if (!hireId || user.role !== 'technician') return;
    try {
      const res = await fetch(`${API_BASE_URL}/technician/hire/${hireId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        showNotice('success', 'Hire request approved.');
        await fetchMessages(selectedConv, false);
        await fetchConversations(false);
      } else {
        const err = await res.json().catch(() => ({}));
        showNotice('error', err.detail || 'Could not approve hire request.');
      }
    } catch {
      showNotice('error', 'Network error.');
    }
  };

  const handleHireInChat = async () => {
    if (!selectedConv || user.role !== 'hirer') return;
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/hire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ technician_id: selectedConv.partnerId }),
      });
      if (res.ok) {
        showNotice('success', 'Hire request sent. Waiting for technician approval.');
        await fetchMessages(selectedConv, false);
        await fetchConversations(false);
      } else {
        const err = await res.json().catch(() => ({}));
        showNotice('error', err.detail || 'Hire failed.');
      }
    } catch {
      showNotice('error', 'Network error.');
    }
  };

  const handleCompleteHire = async (hireId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/hire/${hireId}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        showNotice('success', 'Hire marked as completed!');
        await fetchMessages(selectedConv, false);
      } else {
        const err = await res.json().catch(() => ({}));
        showNotice('error', err.detail || 'Could not complete hire.');
      }
    } catch {
      showNotice('error', 'Network error.');
    }
  };

  const handleEscalate = async () => {
    if (!selectedConv) return;
    const reason = prompt('Please describe why you want to escalate this conversation:');
    if (!reason || !reason.trim()) return;
    try {
      const endpoint = user.role === 'hirer' ? '/hirer/escalate' : '/technician/escalate';
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          partner_id: selectedConv.partnerId,
          partner_role: selectedConv.partnerRole,
          reason: reason.trim(),
        }),
      });
      if (res.ok) {
        showNotice('success', 'Conversation escalated to support.');
      } else {
        const err = await res.json().catch(() => ({}));
        showNotice('error', err.detail || 'Escalation failed.');
      }
    } catch {
      showNotice('error', 'Network error.');
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const stripSystemPrefix = useCallback((content = '') => {
    return content
      .replace(/^\[hirer:\d+\]\s*/, '')
      .replace(/^\[hire-request:\d+\]\s*/, '')
      .replace(/^\[hire:\d+\]\s*/, '')
      .replace(/^\[hire-complete:\d+\]\s*/, '');
  }, []);

  const hireState = useMemo(() => {
    const systemMsgs = messages.filter(
      (m) => m.message_type === 'system' || m.sender_role === 'system'
    );
    if (systemMsgs.length === 0) {
      return { latestPending: null, latestActive: null };
    }

    const completedHireIds = new Set();
    const approvedById = new Map();
    const requestedById = new Map();

    systemMsgs.forEach((m) => {
      const content = m.content || '';
      const completeMatch = content.match(/(?:^|\s)\[hire-complete:(\d+)\]/);
      if (completeMatch?.[1]) {
        completedHireIds.add(completeMatch[1]);
      }
      const approveMatch = content.match(/(?:^|\s)\[hire:(\d+)\]/);
      if (approveMatch?.[1]) {
        approvedById.set(approveMatch[1], m);
      }
      const requestMatch = content.match(/(?:^|\s)\[hire-request:(\d+)\]/);
      if (requestMatch?.[1]) {
        requestedById.set(requestMatch[1], m);
      }
    });

    const pendingRequests = Array.from(requestedById.entries())
      .filter(([hireId]) => !approvedById.has(hireId) && !completedHireIds.has(hireId))
      .map(([, msg]) => msg);

    const activeHires = Array.from(approvedById.entries())
      .filter(([hireId]) => !completedHireIds.has(hireId))
      .map(([, msg]) => msg);

    return {
      latestPending: pendingRequests.length ? pendingRequests[pendingRequests.length - 1] : null,
      latestActive: activeHires.length ? activeHires[activeHires.length - 1] : null,
    };
  }, [messages]);

  const pinnedSystemMessage = useMemo(() => {
    if (hireState.latestActive) return hireState.latestActive;
    return hireState.latestPending;
  }, [hireState]);

  const canCall = useMemo(() => {
    return (
      user.role === 'hirer' &&
      selectedConv?.partnerRole === 'technician' &&
      Boolean(hireState.latestActive)
    );
  }, [user.role, selectedConv, hireState.latestActive]);

  return (
    <div className={`msg-shell${user.role === 'technician' ? ' msg-shell--technician' : ''}`}>
      {user.role === 'technician' && (
        <>
          <header className="msg-techbar">
            <button
              className="msg-techbar__burger"
              onClick={() => setTechMenuOpen((prev) => !prev)}
              aria-label="Toggle technician menu"
            >
              <span />
              <span />
              <span />
            </button>
            <div className="msg-techbar__titleWrap">
              <p className="msg-techbar__eyebrow">Technician Workspace</p>
              <h1 className="msg-techbar__title">SkillArea Technician</h1>
            </div>
            <span className="msg-techbar__ghost">Online</span>
          </header>

          <div className="msg-techtabs">
            <button className="msg-techtabs__item" onClick={() => handleTechMenuNavigate('/technician/dashboard')}>
              Home
            </button>
            <button className="msg-techtabs__item msg-techtabs__item--active" onClick={() => handleTechMenuNavigate('/technician/messages')}>
              Messages
            </button>
            <button className="msg-techtabs__item" onClick={() => handleTechMenuNavigate('/technician/support')}>
              Support
            </button>
          </div>

          <aside className={`msg-techmenu${techMenuOpen ? ' msg-techmenu--open' : ''}`}>
            <button className="msg-techmenu__item" onClick={() => handleTechMenuNavigate('/technician/dashboard')}>Home</button>
            <button className="msg-techmenu__item msg-techmenu__item--active" onClick={() => handleTechMenuNavigate('/technician/messages')}>Messages</button>
            <button className="msg-techmenu__item" onClick={() => handleTechMenuNavigate('/technician/support')}>Support</button>
            <button className="msg-techmenu__item" onClick={() => handleTechMenuNavigate('/technician/dashboard?section=profile')}>Profile</button>
            <button className="msg-techmenu__item" onClick={() => handleTechMenuNavigate('/technician/dashboard?section=jobs')}>Past Hires</button>
            <button className="msg-techmenu__item" onClick={() => handleTechMenuNavigate('/billing')}>Billing & Payment</button>
            <div className="msg-techmenu__divider" />
            <button className="msg-techmenu__item msg-techmenu__item--danger" onClick={handleSignOut}>Sign Out</button>
          </aside>
          {techMenuOpen && <div className="msg-techmenu__overlay" onClick={() => setTechMenuOpen(false)} />}
        </>
      )}

      <div className="msg-page">
      {notice && (
        <div className={`msg-notice msg-notice--${notice.type}`} role="status">
          {notice.text}
        </div>
      )}

      {/* ── Sidebar ── */}
      <div className={`msg-sidebar${mobileShowChat ? ' msg-sidebar--hidden' : ''}`}>
        <div className="msg-sidebar-head">
          <h2 className="msg-sidebar-title">Messages</h2>
          <button className="msg-back-btn" onClick={() => navigate(`/${user.role}/dashboard`)}>
            ← Back
          </button>
        </div>

        {loadingConvs ? (
          <div className="msg-state">
            <div className="msg-spinner" />
            <p>Loading…</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="msg-state">
            <span className="msg-state-icon">💬</span>
            <p>No conversations yet</p>
            <p className="msg-state-hint">
              {user.role === 'hirer'
                ? 'Find a technician and start a conversation.'
                : 'Hirers will message you here.'}
            </p>
          </div>
        ) : (
          <div className="msg-conv-list">
            {conversations.map((conv) => (
              <button
                key={conv.id || String(conv.partnerId)}
                className={`msg-conv-item${selectedConv?.id === conv.id ? ' active' : ''}`}
                onClick={() => selectConversation(conv)}
              >
                <div className="msg-avatar">
                  {conv.partnerName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="msg-conv-body">
                  <div className="msg-conv-top">
                    <span className="msg-conv-name">{conv.partnerName}</span>
                    <span className="msg-conv-time">{formatTime(conv.lastTime)}</span>
                  </div>
                  <div className="msg-conv-preview">
                    {conv.lastMessage?.length > 50
                      ? conv.lastMessage.substring(0, 50) + '…'
                      : conv.lastMessage}
                  </div>
                </div>
                {conv.unreadCount > 0 && (
                  <span className="msg-badge">{conv.unreadCount}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Chat Panel ── */}
      <div className={`msg-chat${!mobileShowChat && !selectedConv ? ' msg-chat--hidden' : ''}`}>
        {!selectedConv ? (
          <div className="msg-no-selection">
            <span className="msg-state-icon">💬</span>
            <h3>Select a conversation</h3>
            <p>Choose someone from the list to start messaging.</p>
          </div>
        ) : (
          <>
            <div className="msg-chat-header">
              <button
                className="msg-mobile-back"
                onClick={() => setMobileShowChat(false)}
                aria-label="Back to conversations"
              >
                ←
              </button>
              <div className="msg-avatar msg-avatar--lg">
                {selectedConv.partnerName?.charAt(0)?.toUpperCase()}
              </div>
              <div className="msg-chat-meta">
                <h3 className="msg-chat-name">{selectedConv.partnerName}</h3>
                <p className="msg-chat-role">
                  {selectedConv.partnerRole}
                </p>
              </div>
              <div className="msg-chat-actions">
                {canCall && partnerPhone && (
                  <a
                    href={`tel:${partnerPhone}`}
                    className="msg-action-btn msg-action-btn--phone"
                    title={`Call ${partnerPhone}`}
                    aria-label="Call"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                    </svg>
                  </a>
                )}
                {user.role === 'hirer' && selectedConv.partnerRole === 'technician' && !hireState.latestActive && !hireState.latestPending && (
                  <button
                    className="msg-action-btn msg-action-btn--hire"
                    onClick={handleHireInChat}
                    title="Send hire request"
                  >
                    Request Hire
                  </button>
                )}
                {user.role === 'technician' && hireState.latestPending && (
                  <button
                    className="msg-action-btn msg-action-btn--hire"
                    onClick={() => {
                      const reqMatch = (hireState.latestPending.content || '').match(/(?:^|\s)\[hire-request:(\d+)\]/);
                      if (reqMatch?.[1]) handleApproveHire(reqMatch[1]);
                    }}
                    title="Approve hire request"
                  >
                    Approve Hire
                  </button>
                )}
                <button
                  className="msg-action-btn msg-action-btn--escalate"
                  onClick={handleEscalate}
                  title="Escalate to support"
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <line x1="4" y1="22" x2="4" y2="15" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="msg-chat-body">
              {pinnedSystemMessage && (
                <div className="msg-system msg-system--pinned">
                  <div className="msg-system__card msg-system__card--pinned">
                    <div className="msg-system__badge">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      Hire Update
                    </div>
                    <p className="msg-system__text">{stripSystemPrefix(pinnedSystemMessage.content)}</p>
                    <span className="msg-system__time">{formatTime(pinnedSystemMessage.created_at)}</span>
                    {(() => {
                      const reqMatch = pinnedSystemMessage.content.match(/(?:^|\s)\[hire-request:(\d+)\]/);
                      const hireMatch = pinnedSystemMessage.content.match(/(?:^|\s)\[hire:(\d+)\]/);
                      const completeMatch = pinnedSystemMessage.content.match(/(?:^|\s)\[hire-complete:(\d+)\]/);
                      if (reqMatch && user.role === 'technician') {
                        return (
                          <button
                            className="msg-system__complete-btn"
                            onClick={() => handleApproveHire(reqMatch[1])}
                          >
                            Approve Hire Request
                          </button>
                        );
                      }
                      if (hireMatch && !completeMatch && user.role === 'hirer') {
                        return (
                          <button
                            className="msg-system__complete-btn"
                            onClick={() => handleCompleteHire(hireMatch[1])}
                          >
                            Mark Hire as Complete
                          </button>
                        );
                      }
                      if (completeMatch) {
                        return <span className="msg-system__completed-tag">Completed</span>;
                      }
                      return null;
                    })()}
                  </div>
                </div>
              )}

              {loadingMsgs ? (
                <div className="msg-msgs-loading">
                  <div className="msg-spinner" />
                </div>
              ) : messages.length === 0 ? (
                <div className="msg-msgs-empty">No messages yet. Say hi! 👋</div>
              ) : (
                messages.map((msg) => {
                  // System / platform messages (hire events)
                  if (msg.message_type === 'system' || msg.sender_role === 'system') {
                    if (pinnedSystemMessage && msg.id === pinnedSystemMessage.id) {
                      return null;
                    }
                    const reqMatch = msg.content.match(/(?:^|\s)\[hire-request:(\d+)\]/);
                    const hireMatch = msg.content.match(/(?:^|\s)\[hire:(\d+)\]/);
                    const completeMatch = msg.content.match(/(?:^|\s)\[hire-complete:(\d+)\]/);
                    const displayText = stripSystemPrefix(msg.content);

                    return (
                      <div key={msg.id} className="msg-system">
                        <div className="msg-system__card">
                          <div className="msg-system__badge">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            SkillArea
                          </div>
                          <p className="msg-system__text">{displayText}</p>
                          <span className="msg-system__time">{formatTime(msg.created_at)}</span>
                          {reqMatch && user.role === 'technician' && (
                            <button
                              className="msg-system__complete-btn"
                              onClick={() => handleApproveHire(reqMatch[1])}
                            >
                              Approve Hire Request
                            </button>
                          )}
                          {hireMatch && !completeMatch && user.role === 'hirer' && (
                            <button
                              className="msg-system__complete-btn"
                              onClick={() => handleCompleteHire(hireMatch[1])}
                            >
                              Mark Hire as Complete
                            </button>
                          )}
                          {completeMatch && (
                            <span className="msg-system__completed-tag">Completed</span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Regular message bubble
                  return (
                    <div
                      key={msg.id}
                      className={`msg-bubble${Number(msg.sender_id) === currentUserId ? ' sent' : ' received'}`}
                    >
                      {msg.message_type === 'image' && msg.media_url && (
                        <img
                          src={`${API_BASE_URL}${msg.media_url}`}
                          alt="Shared"
                          className="msg-bubble-media msg-bubble-img"
                          onClick={() => window.open(`${API_BASE_URL}${msg.media_url}`, '_blank')}
                        />
                      )}
                      {msg.message_type === 'video' && msg.media_url && (
                        <video
                          src={`${API_BASE_URL}${msg.media_url}`}
                          controls
                          className="msg-bubble-media msg-bubble-video"
                          preload="metadata"
                        />
                      )}
                      {(() => {
                        const cleanContent = msg.content || '';
                        if ((msg.message_type === 'image' && cleanContent === '[image]') ||
                            (msg.message_type === 'video' && cleanContent === '[video]')) {
                          return null;
                        }
                        return <div className="msg-bubble-text">{cleanContent}</div>;
                      })()}
                      <div className="msg-bubble-time">{formatTime(msg.created_at)}</div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {mediaPreview && (
              <div className="msg-media-preview">
                {mediaPreview.type === 'image' ? (
                  <img src={mediaPreview.url} alt="Preview" className="msg-media-preview__img" />
                ) : (
                  <video src={mediaPreview.url} className="msg-media-preview__vid" controls />
                )}
                <button className="msg-media-preview__close" onClick={clearMediaPreview} aria-label="Remove">&times;</button>
              </div>
            )}

            <form className="msg-input-row" onSubmit={sendMessage}>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={handleMediaSelect}
              />
              <button
                type="button"
                className="msg-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploading}
                aria-label="Attach media"
                title="Attach image or video"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <input
                className="msg-input"
                type="text"
                placeholder={uploading ? 'Uploading…' : 'Type a message…'}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sending || uploading}
                autoComplete="off"
              />
              <button
                type="submit"
                className="msg-send-btn"
                disabled={(!newMessage.trim() && !mediaPreview) || sending || uploading}
                aria-label="Send message"
              >
                {sending || uploading ? (
                  <span className="msg-spinner msg-spinner--sm" />
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </form>
          </>
        )}
      </div>
      </div>
    </div>
  );
}

export default Messages;
